import React, { useEffect, useRef, useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import SkeletonLoader from "@/components/ui/SkeletonLoader";

const GRID_LINES = 6;

const TrackingChart = ({
  data,
  title,
  queryColumn,
  dateColumns = [],
  selectedQuery = null,
  additionalInfo = null, // New prop for additional information
  isLoaded = false,
  error = null,
  height = 300,
  colors,
  className = "",
  noDataMessage,
}) => {
  const { t, i18n } = useTranslation();
  const resolvedNoDataMessage =
    noDataMessage ?? t("common.noDataGeneric", "No data available");
  const canvasRef = useRef(null);
  const containerRef = useRef(null);
  const [containerSize, setContainerSize] = useState({ width: 0, height });
  const [tooltip, setTooltip] = useState(null);

  // Resolve theme palette from CSS custom properties
  const themeColors = useMemo(() => {
    const s = getComputedStyle(document.documentElement);
    const get = (v) => s.getPropertyValue(v).trim();
    return [
      get("--brand-color-1"),
      get("--brand-color-2"),
      get("--text-primary"),
      get("--brand-color-dark"),
      get("--accent-lime"),
      get("--success"),
      get("--warning"),
      get("--error"),
      get("--info-light"),
      get("--funnel-top-bg"),
      get("--warning-light"),
      get("--success-light"),
      get("--error-light"),
      get("--delete-red"),
      get("--badge-unbranded-bg"),
    ];
  }, []);

  const resolvedColors = colors ?? themeColors;

  // Process chart data
  const chartData = React.useMemo(() => {
    if (!data || data.length === 0) return { lines: [], dates: [] };

    // Filter data: if selectedQuery is provided, show only that query, otherwise show all
    const filteredData = selectedQuery
      ? data.filter((item) => item[queryColumn] === selectedQuery)
      : data;

    // Extract dates (all columns except the query column)
    const dates = dateColumns.length > 0 ? dateColumns : [];

    // Create line data for each query
    const lines = filteredData.map((item, index) => {
      const points = dates.map((date) => {
        const value = item[date];
        // Handle "-" or null values as null
        return value === "-" || value === null || value === undefined
          ? null
          : Number(value);
      });

      return {
        label: item[queryColumn],
        points,
        color: resolvedColors[index % resolvedColors.length],
      };
    });

    return { lines, dates };
  }, [data, queryColumn, dateColumns, selectedQuery, resolvedColors]);

  // Helper function to format date for display
  const formatDateTracked = (dateString) => {
    if (!dateString) return null;

    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) {
        return "Invalid date";
      }
      return date.toLocaleDateString(i18n.resolvedLanguage || i18n.language || "en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
      });
    } catch (e) {
      return "Invalid date";
    }
  };

  // Handle mouse move for tooltip
  const handleMouseMove = (e) => {
    if (!canvasRef.current || !chartData.lines.length) return;

    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    // Use CSS pixels for hit testing (drawing uses CSS units after scaling)
    const mouseX = e.clientX - rect.left;
    const mouseY = e.clientY - rect.top;

    const padding = { top: 30, right: 30, bottom: 60, left: 60 };
    const chartWidth = rect.width - padding.left - padding.right;
    const chartHeight = rect.height - padding.top - padding.bottom;

    // Find min and max values
    let minValue = Infinity;
    let maxValue = -Infinity;

    chartData.lines.forEach((line) => {
      line.points.forEach((point) => {
        if (point !== null) {
          minValue = Math.min(minValue, point);
          maxValue = Math.max(maxValue, point);
        }
      });
    });

    if (!Number.isFinite(minValue) || !Number.isFinite(maxValue)) {
      setTooltip(null);
      return;
    }

    const valueRange = maxValue - minValue;
    const paddingAmount =
      valueRange > 0 ? valueRange * 0.15 : Math.max(1, Math.abs(minValue) * 0.15);
    let paddedMin = Math.floor(Math.max(0, minValue - paddingAmount));
    let paddedMax = Math.ceil(maxValue + paddingAmount);
    // Ensure at least 1 unit per grid line so rounded integer labels don't repeat
    if (paddedMax - paddedMin < GRID_LINES) {
      paddedMax = paddedMin + GRID_LINES;
    }

    const getY = (value) => {
      if (value === null) return null;
      const ratio = (value - paddedMin) / (paddedMax - paddedMin);
      return padding.top + chartHeight - ratio * chartHeight;
    };

    const getX = (index) => {
      return padding.left + (index * chartWidth) / (chartData.dates.length - 1);
    };

    // Check if mouse is near any point
    let closestPoint = null;
    let minDistance = 10; // Hover threshold in pixels

    chartData.lines.forEach((line) => {
      line.points.forEach((point, index) => {
        if (point !== null) {
          const x = getX(index);
          const y = getY(point);
          const distance = Math.sqrt(
            Math.pow(mouseX - x, 2) + Math.pow(mouseY - y, 2),
          );

          if (distance < minDistance) {
            minDistance = distance;

            // Get additional info for this label if available
            const itemAdditionalInfo =
              additionalInfo && additionalInfo[line.label]
                ? additionalInfo[line.label]
                : null;

            closestPoint = {
              label: line.label,
              value: point,
              date: chartData.dates[index],
              x: e.clientX - rect.left,
              y: e.clientY - rect.top,
              color: line.color,
              additionalInfo: itemAdditionalInfo, // Add additional info to tooltip data
            };
          }
        }
      });
    });

    setTooltip(closestPoint);
  };

  const handleMouseLeave = () => {
    setTooltip(null);
  };

  // Observe container size so we can make the canvas match rendered size
  useEffect(() => {
    if (!containerRef.current) return;

    const setSize = () => {
      const el = containerRef.current;
      if (!el) return;
      setContainerSize({ width: el.clientWidth, height });
    };

    setSize();

    const ro = new ResizeObserver(() => setSize());
    ro.observe(containerRef.current);

    return () => ro.disconnect();
  }, [height]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    // Compute CSS pixel dimensions (what the canvas is displayed as)
    const cssWidth =
      containerSize.width ||
      containerRef.current?.clientWidth ||
      canvas.clientWidth ||
      800;
    const cssHeight = containerSize.height || height;

    const dpr = window.devicePixelRatio || 1;
    // Set internal pixel size to CSS size * DPR for sharp rendering
    canvas.width = Math.max(1, Math.floor(cssWidth * dpr));
    canvas.height = Math.max(1, Math.floor(cssHeight * dpr));
    // Scale drawing operations so we can use CSS pixel coordinates below
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    // Clear in CSS coordinates
    ctx.clearRect(0, 0, cssWidth, cssHeight);

    // Resolve theme colors for canvas drawing
    const docStyle = getComputedStyle(document.documentElement);
    const colorBorder =
      docStyle.getPropertyValue("--border").trim() || "#e2e8f0";
    const colorTextSecondary =
      docStyle.getPropertyValue("--text-secondary").trim() || "#4b5563";
    const colorBgElevated =
      docStyle.getPropertyValue("--bg-elevated").trim() || "#ffffff";

    // Return early if no data or not loaded
    if (!chartData.lines.length || !isLoaded) return;

    const padding = { top: 30, right: 30, bottom: 60, left: 60 };
    const chartWidth = cssWidth - padding.left - padding.right;
    const chartHeight = cssHeight - padding.top - padding.bottom;

    // Find min and max values across all lines
    let minValue = Infinity;
    let maxValue = -Infinity;

    chartData.lines.forEach((line) => {
      line.points.forEach((point) => {
        if (point !== null) {
          minValue = Math.min(minValue, point);
          maxValue = Math.max(maxValue, point);
        }
      });
    });

    if (!Number.isFinite(minValue) || !Number.isFinite(maxValue)) return;

    // Add padding to min/max for better visualization
    const valueRange = maxValue - minValue;
    const paddingAmount =
      valueRange > 0 ? valueRange * 0.15 : Math.max(1, Math.abs(minValue) * 0.15);
    let paddedMin = Math.floor(Math.max(0, minValue - paddingAmount));
    let paddedMax = Math.ceil(maxValue + paddingAmount);
    // Ensure at least 1 unit per grid line so rounded integer labels don't repeat
    if (paddedMax - paddedMin < GRID_LINES) {
      paddedMax = paddedMin + GRID_LINES;
    }

    // Helper function to map value to Y coordinate
    const getY = (value) => {
      if (value === null) return null;
      const ratio = (value - paddedMin) / (paddedMax - paddedMin);
      return padding.top + chartHeight - ratio * chartHeight;
    };

    // Helper function to get X coordinate
    const getX = (index) => {
      return padding.left + (index * chartWidth) / (chartData.dates.length - 1);
    };

    // Draw horizontal grid lines
    ctx.strokeStyle = colorBorder;
    ctx.lineWidth = 1;

    for (let i = 0; i <= GRID_LINES; i++) {
      const value = paddedMin + (i * (paddedMax - paddedMin)) / GRID_LINES;
      const y = getY(value);

      ctx.beginPath();
      ctx.moveTo(padding.left, y);
      ctx.lineTo(padding.left + chartWidth, y);
      ctx.stroke();
    }

    // Draw Y-axis labels
    ctx.fillStyle = colorTextSecondary;
    ctx.font = "12px -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif";
    ctx.textAlign = "right";
    ctx.textBaseline = "middle";

    for (let i = 0; i <= GRID_LINES; i++) {
      const value = paddedMin + (i * (paddedMax - paddedMin)) / GRID_LINES;
      const y = getY(value);
      ctx.fillText(Math.round(value).toString(), padding.left - 15, y);
    }

    // Draw X-axis labels
    ctx.textAlign = "center";
    ctx.textBaseline = "top";
    chartData.dates.forEach((date, index) => {
      const x = getX(index);
      ctx.fillText(date, x, cssHeight - padding.bottom + 15);
    });

    // Draw each line
    chartData.lines.forEach((line) => {
      ctx.strokeStyle = line.color;
      ctx.lineWidth = 2.5;
      ctx.lineCap = "round";
      ctx.lineJoin = "round";

      // Draw line segments
      ctx.beginPath();
      let isFirstPoint = true;

      line.points.forEach((point, index) => {
        if (point !== null) {
          const x = getX(index);
          const y = getY(point);

          if (isFirstPoint) {
            ctx.moveTo(x, y);
            isFirstPoint = false;
          } else {
            ctx.lineTo(x, y);
          }
        } else {
          isFirstPoint = true;
        }
      });

      ctx.stroke();

      // Draw data points
      line.points.forEach((point, index) => {
        if (point !== null) {
          const x = getX(index);
          const y = getY(point);

          // Draw filled circle
          ctx.beginPath();
          ctx.arc(x, y, 4, 0, 2 * Math.PI);
          ctx.fillStyle = line.color;
          ctx.fill();

          // Draw white inner circle for contrast
          ctx.beginPath();
          ctx.arc(x, y, 2, 0, 2 * Math.PI);
          ctx.fillStyle = colorBgElevated;
          ctx.fill();
        }
      });
    });
  }, [chartData, height, isLoaded, containerSize.width, containerSize.height]);

  return (
    <div
      className={`bg-white border card-radius${className ? ` ${className}` : ""}`}
      style={{
        borderColor: "var(--border)",
        boxShadow: "0 6px 18px rgba(0,0,0,0.06)",
        overflow: "hidden",
      }}
    >
      {title && (
        <div
          className="px-3 py-2 border-bottom"
          style={{ backgroundColor: "var(--bg-surface)" }}
        >
          <div className="fw-semibold text-dark" style={{ fontSize: "14px" }}>
            {title}
          </div>
        </div>
      )}

      <div className="p-3">
        {/* Error State */}
        {error && (
          <div className="text-center">
            <div className="alert alert-danger mb-0" role="alert">
              <i className="bi bi-exclamation-triangle me-2"></i>
              {t(
                "common.loadError",
                "An error occurred while loading the data. Please try again later.",
              )}
            </div>
          </div>
        )}

        {/* Loading State */}
        {!error && !isLoaded && (
          <div
            className="d-flex flex-column justify-content-center"
            style={{ minHeight: `${height}px` }}
          >
            <SkeletonLoader width="100%" height="2px" className="mb-4" />
            <SkeletonLoader width="100%" height="2px" className="mb-4" />
            <SkeletonLoader width="100%" height="2px" className="mb-4" />
            <SkeletonLoader width="100%" height="2px" className="mb-4" />
            <div className="d-flex justify-content-between mt-auto">
              <SkeletonLoader width="18%" height="10px" />
              <SkeletonLoader width="18%" height="10px" />
              <SkeletonLoader width="18%" height="10px" />
              <SkeletonLoader width="18%" height="10px" />
            </div>
          </div>
        )}

        {/* Loaded State with Data */}
        {!error && isLoaded && chartData.lines.length > 0 && (
          <div
            style={{ position: "relative", width: "100%" }}
            ref={containerRef}
          >
            <canvas
              ref={canvasRef}
              className="w-100"
              style={{
                width: "100%",
                maxWidth: "100%",
                height: `${height}px`,
                cursor: tooltip ? "pointer" : "default",
              }}
              onMouseMove={handleMouseMove}
              onMouseLeave={handleMouseLeave}
            />

            {/* Tooltip */}
            {tooltip && (
              <div
                style={{
                  position: "absolute",
                  left: `${tooltip.x}px`,
                  top: `${tooltip.y - (tooltip.additionalInfo && tooltip.additionalInfo.date_tracked ? 80 : 70)}px`,
                  transform: "translateX(-50%)",
                  backgroundColor: "var(--bg-elevated)",
                  color: "var(--text-primary)",
                  padding: "10px 14px",
                  borderRadius: "6px",
                  fontSize: "13px",
                  pointerEvents: "none",
                  whiteSpace: "nowrap",
                  zIndex: 1000,
                  boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
                  border: "1px solid var(--border)",
                }}
              >
                <div
                  style={{
                    fontWeight: "600",
                    marginBottom: "4px",
                    color: tooltip.color,
                  }}
                >
                  {tooltip.label}
                </div>
                <div
                  style={{ fontSize: "12px", color: "var(--text-secondary)" }}
                >
                  {tooltip.date}:{" "}
                  <strong style={{ color: "var(--text-primary)" }}>
                    {tooltip.value}
                  </strong>
                </div>

                {/* Display additional info if available */}
                {tooltip.additionalInfo &&
                  tooltip.additionalInfo.date_tracked && (
                    <div
                      style={{
                        fontSize: "11px",
                        color: "var(--text-muted)",
                        marginTop: "6px",
                        paddingTop: "6px",
                        borderTop: "1px solid var(--border)",
                      }}
                    >
                      <span style={{ fontWeight: "500" }}>
                        {t("tracking.trackedSince", "Tracked since:")}
                      </span>{" "}
                      {formatDateTracked(tooltip.additionalInfo.date_tracked)}
                    </div>
                  )}
              </div>
            )}
          </div>
        )}

        {/* No Data State */}
        {!error && isLoaded && chartData.lines.length === 0 && (
          <div
            className="text-center text-muted"
            style={{ minHeight: `${height}px` }}
          >
            <div className="d-flex justify-content-center align-items-center h-100">
              <div>
                <i className="bi bi-graph-up me-2"></i>
                {resolvedNoDataMessage}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default TrackingChart;
